import { useEffect, useState } from "react";
import { Check, Quote, ChevronDown } from "lucide-react";
import Reveal from "@/lib/Reveal";
import { SectionLabel, BuyButton, GoldRule, GlowPhoto } from "@/lib/ui";
import {
  HERO_IMAGE,
  RED_MIRROR_IMAGE,
  CIRCLE_IMAGE,
  CRYSTALS_IMAGE,
  LAUNCH_DATE_ISO,
  cta,
  phases,
  included,
  testimonials,
} from "@/lib/siteContent";

const U = ({ children, color = "decoration-gold" }) => (
  <span className={`underline decoration-2 underline-offset-4 ${color}`}>{children}</span>
);
const B = ({ children, color = "text-ruby" }) => (
  <strong className={`font-semibold ${color}`}>{children}</strong>
);
const S = ({ children, color = "text-coral" }) => (
  <span className={`font-script text-[1.7em] leading-none ${color}`}>{children}</span>
);

export const Hero = () => (
  <section data-testid="hero-section" className="relative">
    <div className="flex flex-col lg:grid lg:min-h-[92vh] lg:grid-cols-2">
      <div className="relative order-1 h-[55vh] min-h-[380px] w-full overflow-hidden lg:order-2 lg:h-auto">
        <img
          data-testid="hero-image"
          src={HERO_IMAGE}
          alt="J Muenz leading a workshop"
          className="absolute inset-0 h-full w-full object-cover object-[50%_20%]"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-cream via-transparent to-transparent lg:bg-gradient-to-r lg:from-cream lg:via-transparent lg:to-transparent"
          aria-hidden="true"
        />
      </div>
      <div className="relative order-2 flex flex-col items-center justify-center px-6 py-16 text-center lg:order-1 lg:items-end lg:py-24 lg:pr-14">
        <Reveal>
          <span className="inline-block bg-ruby px-5 py-1.5 font-body text-xs sm:text-sm font-medium uppercase tracking-[0.45em] text-white">
            The
          </span>
          <h1 className="relative mt-4 inline-block bg-white px-8 py-4 font-heading text-5xl sm:text-7xl lg:text-7xl xl:text-8xl font-semibold uppercase tracking-tight text-ruby shadow-[0_20px_60px_rgba(184,21,74,0.15)]">
            Abundance
          </h1>
          <p className="relative z-10 mt-2 font-script text-6xl text-gold drop-shadow-[0_0_18px_rgba(212,175,55,0.55)] sm:text-8xl">
            Machine
          </p>
        </Reveal>
        <Reveal delay={150}>
          <h2 className="mx-auto mt-8 inline-block max-w-xl bg-ruby px-6 py-3 font-heading text-xl italic text-cream sm:text-2xl">
            Let's see how much you can <U color="decoration-gold-light">manifest</U>{" "}
            in the next <B color="text-gold-light">21 days</B>... and have a whole
            lotta <S color="text-gold-light">fun</S> doing it!
          </h2>
        </Reveal>
        <div
          className="mt-12 flex flex-col items-center"
          aria-hidden="true"
        >
          <ChevronDown className="h-6 w-6 animate-bounce text-ruby" strokeWidth={3} />
          <ChevronDown
            className="-mt-3 h-6 w-6 animate-bounce text-ruby/60 [animation-delay:150ms]"
            strokeWidth={3}
          />
          <ChevronDown
            className="-mt-3 h-6 w-6 animate-bounce text-ruby/30 [animation-delay:300ms]"
            strokeWidth={3}
          />
        </div>
      </div>
    </div>
    <div className="mx-auto max-w-3xl px-6 pb-16 pt-4 text-center sm:pb-24">
      <Reveal>
        <p className="mx-auto max-w-2xl font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          A <B>21-day experience</B> where we use <U>subconscious recoding</U>,{" "}
          <U color="decoration-coral">energy and frequency practices</U>, and
          intentional daily experiments to see what happens when you start{" "}
          <B>expecting</B>, <B>believing in</B> and <B>making room for more</B>.
        </p>
        <p className="mx-auto mt-8 whitespace-nowrap font-heading text-[clamp(0.72rem,2vw,1.9rem)] italic text-ruby">
          Let's see what happens when you become really f*cking{" "}
          <U color="decoration-coral">good at receiving</U>.
        </p>
        <div className="mt-10">
          <BuyButton testId="cta-hero-button">{cta.hero}</BuyButton>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Pitch = () => (
  <section data-testid="pitch-section" className="relative overflow-hidden py-20 sm:py-28">
    <img
      src={`${process.env.PUBLIC_URL || ""}/glitter-left.png`}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute left-0 top-0 hidden h-full w-40 object-cover lg:block xl:w-56"
    />
    <img
      src={`${process.env.PUBLIC_URL || ""}/glitter-right.png`}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute right-0 top-0 hidden h-full w-40 object-cover lg:block xl:w-56"
    />
    <div className="relative mx-auto max-w-3xl px-6">
      <Reveal>
        <p className="font-heading text-2xl sm:text-3xl leading-snug text-charcoal">
          You've probably had moments when something came together so{" "}
          <U>perfectly</U> that you thought,{" "}
          <em>There's no way that was a coincidence.</em>
        </p>
        <p className="mt-6 font-heading text-2xl sm:text-3xl text-charcoal">
          And you want <B color="text-ruby">MORE</B> of those moments.
        </p>
        <p className="mt-6 font-body text-base sm:text-lg leading-relaxed text-charcoal">
          Because you <B>love</B> seeing what's possible. And lately, you've been
          wondering...
        </p>
      </Reveal>
      <Reveal delay={100}>
        <div className="mt-10 -rotate-1 bg-ruby px-7 py-8 shadow-[0_24px_70px_rgba(184,21,74,0.25)] sm:px-10 sm:py-10">
          <div className="rotate-1 space-y-5">
            <p className="font-heading text-xl sm:text-2xl italic leading-snug text-cream">
              What if the next level of <U color="decoration-gold-light">abundance</U>{" "}
              is about changing the{" "}
              <B color="text-gold-light">thoughts, beliefs and energy</B> you're
              bringing into everything you do?!
            </p>
            <p className="font-heading text-xl sm:text-2xl italic leading-snug text-cream">
              What if you could become just as good at{" "}
              <U color="decoration-gold-light">receiving</U> as you are at{" "}
              <U color="decoration-gold-light">creating</U>?!
            </p>
            <p className="font-heading text-xl sm:text-2xl italic leading-snug text-cream">
              What if things could happen{" "}
              <B color="text-gold-light">faster, easier</B> and in ways you couldn't
              have planned if you tried?
            </p>
          </div>
        </div>
        <p className="mt-8 text-center font-heading text-2xl italic text-ruby sm:text-3xl">
          And what if you had <S>FUN</S> finding out?!
        </p>
      </Reveal>
      <Reveal delay={150}>
        <p className="mt-10 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          That's what I want to <U color="decoration-coral">explore with you</U>. I
          have a little <B>experiment</B> I want to run. For the next 21 days, I want
          you to get intentional about what you're <B>thinking, believing, expecting</B>{" "}
          and allowing yourself to receive.
        </p>
        <GlowPhoto
          src={CRYSTALS_IMAGE}
          alt="J Muenz pulling cards surrounded by crystals and candlelight"
          testId="pitch-photo"
        />
        <p className="mt-12 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          We're going to work with your <U>subconscious mind</U> and use energy and
          frequency practices to explore how your <B>internal state</B> influences the
          way you experience your life.
        </p>
        <p className="mt-5 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          We're going to practice new beliefs around{" "}
          <B>money, opportunity, receiving</B> and what's possible for you.
        </p>
        <p className="mt-10 text-center font-heading text-3xl font-bold text-ruby sm:text-4xl">
          And we're going to pay attention.
        </p>
        <p className="mt-6 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          To the unexpected money, invitations, convo's and opportunities that seem to{" "}
          <em>"come out of nowhere."</em> The moments when you follow an internal
          nudge and something <U color="decoration-coral">really insane</U> happens!
        </p>
        <p className="mt-5 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          I want you to collect <U>evidence of what's possible</U>. Because that's
          where things get really <S>cray</S>.
        </p>
        <div className="mt-10 text-center">
          <BuyButton testId="cta-pitch-button">{cta.pitch}</BuyButton>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Vision = () => (
  <section data-testid="vision-section" className="relative bg-sand py-20 sm:py-28">
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.10]"
      style={{
        backgroundImage:
          "url(https://images.unsplash.com/photo-1656055450481-e47bc720e687?crop=entropy&cs=srgb&fm=jpg&q=85)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      aria-hidden="true"
    />
    <div className="relative mx-auto max-w-3xl px-6">
      <Reveal className="text-center">
        <SectionLabel>The Experiment</SectionLabel>
        <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-charcoal">
          What could happen in 21 days…
        </h2>
        <div className="mt-8">
          <GoldRule />
        </div>
      </Reveal>
      <div className="mt-12 space-y-6">
        <Reveal>
          <p className="font-body text-base sm:text-lg leading-relaxed text-charcoal">
            Imagine opening your email and finding an <B>opportunity</B> you weren't{" "}
            <U>expecting</U>.
          </p>
        </Reveal>
        <Reveal delay={60}>
          <p className="font-body text-base sm:text-lg leading-relaxed text-charcoal">
            Receiving a <B>compliment, gift, or offer of support</B> w/o immediately
            trying to <U color="decoration-coral">give something back</U>.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <p className="font-body text-base sm:text-lg leading-relaxed text-charcoal">
            Finally moving on an <U>idea</U> you've been sitting on for{" "}
            <B>months</B>.
          </p>
        </Reveal>
        <Reveal delay={180}>
          <p className="font-body text-base sm:text-lg leading-relaxed text-charcoal">
            Feeling <B color="text-coral">excited</B> about what might happen today
            instead of thinking about everything you need to get done.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <p className="font-body text-base sm:text-lg leading-relaxed text-charcoal">
            Recognizing <U>opportunities</U> you might have overlooked before. And
            starting to notice you're becoming a woman who{" "}
            <B>expects incredible things</B> to happen for her.
          </p>
        </Reveal>
      </div>
      <Reveal delay={200}>
        <p className="mt-12 text-center font-heading text-xl sm:text-2xl italic leading-snug text-charcoal">
          Because she's deliberately changing the way she thinks, feels, decides and
          participates in her life. And THAT is where I want to take you.
        </p>
        <p className="mt-10 text-center font-script text-5xl text-ruby sm:text-6xl">
          Welcome to your 21-day abundance experiment
        </p>
        <p className="mx-auto mt-6 max-w-2xl text-center font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          No live calls, complicated assignments or adding another massive commitment
          to your calendar.
        </p>
        <p className="mx-auto mt-5 max-w-2xl text-center font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          Just <B>21 days of guided practices</B> that help you become more{" "}
          <U color="decoration-coral">intentional</U> about the energy, beliefs, and
          expectations you're bringing into your life.
        </p>
        <div className="mt-12">
          <GlowPhoto
            src={CIRCLE_IMAGE}
            alt="Women gathered in circle at one of J's retreats"
            testId="circle-photo"
            soft
          />
        </div>
      </Reveal>
    </div>
  </section>
);

export const Phases = () => (
  <section data-testid="phases-section" className="py-20 sm:py-28">
    <div className="mx-auto max-w-3xl px-6">
      <Reveal className="text-center">
        <SectionLabel>The Journey</SectionLabel>
        <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-charcoal">
          Here's how we're doing it…
        </h2>
      </Reveal>
      <div className="mt-16 space-y-16">
        {phases.map((phase, i) => (
          <Reveal key={phase.title} delay={i * 80}>
            <div data-testid={`phase-${i + 1}`}>
              <p className="font-body text-xs sm:text-sm font-semibold uppercase tracking-[0.3em] text-gold">
                {phase.days}
              </p>
              <h3 className="mt-2 inline-block bg-ruby px-4 py-1.5 font-heading text-2xl uppercase tracking-wide text-cream sm:text-3xl">
                {phase.title}
              </h3>
              <p className="mt-4 font-heading text-xl sm:text-2xl italic text-charcoal">
                {phase.subtitle}
              </p>
              <div className="mt-5 space-y-4">
                {phase.body.map((para, j) => (
                  <p key={j} className="font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
                    {para}
                  </p>
                ))}
              </div>
              {i < phases.length - 1 && (
                <div className="mt-14">
                  <GoldRule />
                </div>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Included = () => (
  <section data-testid="included-section" className="relative overflow-hidden bg-white py-20 sm:py-28">
    <img
      src={`${process.env.PUBLIC_URL || ""}/glitter-left.png`}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute left-0 top-0 hidden h-full w-40 object-cover lg:block xl:w-56"
    />
    <img
      src={`${process.env.PUBLIC_URL || ""}/glitter-right.png`}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute right-0 top-0 hidden h-full w-40 object-cover lg:block xl:w-56"
    />
    <div className="relative mx-auto max-w-3xl px-6">
      <Reveal className="text-center">
        <SectionLabel>Inside The Experience</SectionLabel>
        <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-charcoal">
          Here's what you're stepping into…
        </h2>
      </Reveal>
      <div className="mt-16 space-y-12">
        {included.map((item, i) => (
          <Reveal key={item.title} delay={i * 60}>
            <div data-testid={`included-${i + 1}`} className="flex items-start gap-5">
              <span className="mt-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold/15">
                <Check className="h-4 w-4 text-gold" aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-body text-sm sm:text-base font-semibold uppercase tracking-[0.2em] text-charcoal">
                  {item.title}
                </h3>
                <p className="mt-3 font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
                  {item.body}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Countdown = () => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const target = new Date(LAUNCH_DATE_ISO).getTime();
  const diff = Math.max(0, target - now);
  const started = diff === 0;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  const units = [
    { label: "Days", value: days },
    { label: "Hours", value: hours },
    { label: "Minutes", value: minutes },
    { label: "Seconds", value: seconds },
  ];

  if (started) {
    return (
      <p
        data-testid="countdown-started"
        className="mt-8 font-heading text-2xl sm:text-3xl italic text-ruby"
      >
        The doors are open... and the price is now $297.
      </p>
    );
  }

  return (
    <div data-testid="countdown" className="mt-10">
      <div className="flex items-center justify-center gap-3 sm:gap-5">
        {units.map((unit, i) => (
          <div key={unit.label} className="flex items-center gap-3 sm:gap-5">
            <div className="flex w-16 flex-col items-center rounded-2xl border border-gold-border bg-white px-2 py-4 shadow-[0_10px_40px_rgba(197,160,89,0.12)] sm:w-20">
              <span className="font-heading text-2xl font-semibold tabular-nums text-ruby sm:text-3xl">
                {String(unit.value).padStart(2, "0")}
              </span>
              <span className="mt-1 font-body text-[10px] font-medium uppercase tracking-[0.2em] text-mutedwarm sm:text-xs">
                {unit.label}
              </span>
            </div>
            {i < units.length - 1 && (
              <span className="font-heading text-2xl text-gold" aria-hidden="true">
                :
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="mx-auto mt-5 max-w-md font-body text-xs sm:text-sm uppercase tracking-[0.25em] text-mutedwarm">
        until we begin... and the price goes up to $297
      </p>
    </div>
  );
};

export const Pricing = () => (
  <section data-testid="pricing-section" className="relative overflow-hidden py-20 sm:py-28">
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.12]"
      style={{
        backgroundImage:
          "url(https://images.unsplash.com/photo-1656055450481-e47bc720e687?crop=entropy&cs=srgb&fm=jpg&q=85)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      aria-hidden="true"
    />
    <img
      src={`${process.env.PUBLIC_URL || ""}/glitter-left.png`}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute left-0 top-0 hidden h-full w-40 object-cover lg:block xl:w-56"
    />
    <img
      src={`${process.env.PUBLIC_URL || ""}/glitter-right.png`}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute right-0 top-0 hidden h-full w-40 object-cover lg:block xl:w-56"
    />
    <div className="relative mx-auto max-w-2xl px-6 text-center">
      <Reveal>
        <SectionLabel>Your Investment</SectionLabel>
        <p className="mt-6 font-heading text-xl sm:text-2xl italic text-charcoal">
          We start <span className="text-ruby">November 30th</span>... and that's when
          the price goes up to $297.
        </p>
        <Countdown />
        <div className="mt-8 flex items-end justify-center gap-4">
          <span className="font-heading text-6xl sm:text-7xl font-semibold text-ruby">
            $97
          </span>
          <span className="pb-2 font-heading text-2xl sm:text-3xl text-mutedwarm line-through">
            $297
          </span>
        </div>
        <p className="mt-4 font-body text-xs sm:text-sm font-medium uppercase tracking-[0.3em] text-gold">
          Regular $297
        </p>
        <p className="mx-auto mt-8 max-w-xl font-body text-base sm:text-lg leading-relaxed text-mutedwarm">
          21 daily audio experiences infused with subconscious recoding, energy and
          frequency practices and daily abundance experiments. Plus your personal
          abundance tracker.
        </p>
        <div className="mt-10">
          <BuyButton testId="cta-pricing-button">{cta.pricing}</BuyButton>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Testimonials = () => (
  <section data-testid="testimonials-section" className="relative overflow-hidden bg-ruby py-20 sm:py-28">
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.10]"
      style={{
        backgroundImage:
          "url(https://images.unsplash.com/photo-1656055450481-e47bc720e687?crop=entropy&cs=srgb&fm=jpg&q=85)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      aria-hidden="true"
    />
    <div className="relative mx-auto max-w-3xl px-6">
      <Reveal className="text-center">
        <p
          data-testid="section-label"
          className="font-body text-xs sm:text-sm font-medium uppercase tracking-[0.3em] text-gold-light"
        >
          Client Love
        </p>
        <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-cream">
          Here's what happens when women start believing in themselves whole-heartedly.
        </h2>
        <p className="mx-auto mt-6 max-w-2xl font-body text-base sm:text-lg leading-relaxed text-cream/85">
          I've spent the last 14 years working with women on their subconscious
          beliefs, emotional patterns and the ways they experience themselves and
          their lives. And some of my favorite moments are when they start recognizing
          their own power. Here's what women I've worked with have shared:
        </p>
      </Reveal>
      <div className="mt-14 space-y-10">
        {testimonials.map((quote, i) => (
          <Reveal key={i} delay={i * 60}>
            <figure data-testid={`testimonial-${i + 1}`} className="flex items-start gap-4">
              <Quote className="mt-1 h-6 w-6 shrink-0 rotate-180 text-gold-light" aria-hidden="true" />
              <blockquote className="font-heading text-xl sm:text-2xl italic leading-snug text-cream">
                "{quote}"
              </blockquote>
            </figure>
          </Reveal>
        ))}
      </div>
      <Reveal delay={150}>
        <p className="mx-auto mt-12 max-w-2xl text-center font-body text-base sm:text-lg leading-relaxed text-cream/85">
          These women are describing changes in how they trust themselves, express
          themselves and move with that trust through their lives, relationships and
          businesses. When you change how you show up, you can change the decisions
          you make and the opportunities you pursue.
        </p>
      </Reveal>
    </div>
  </section>
);
